import { ProjectModel } from "@bryntum/schedulerpro";
import CrudQueue from "@/lib/CrudQueue.ts";
import { sendCustomMessageOnSentry } from "@/sentryIntegration.jsx";

const willChange = (fieldName, rawData, record) => {
  try {
    const field2 = record.getFieldDefinition(fieldName === record.constructor.idField ? "id" : fieldName), { dataSource } = field2, newValue = record.constructor.processField(fieldName, rawData[dataSource], record);
    return dataSource in rawData ? !field2.isEqual(newValue, record.getValue(fieldName)) : true;
  } catch (e) {
    console.log(e, "Override To Handle Exception");
  }
  return false;
};

export class SwivuProjectModel extends ProjectModel {
  // After dragging an event, when we try to manipulate that event through the applyChangeset event constraints prevents it from being updated
  addConstraintOnDateSet = false;

  crudQueue = new CrudQueue(undefined, {
    onTimeout: (details) => sendCustomMessageOnSentry({
      messageTitle: "CRUD queue step timed out; executor may still be running",
      logType: "warning",
      extraDataBody: { ...details, projectId: this.id }
    }),
    onError: (error, details) => sendCustomMessageOnSentry({
      messageTitle: "CRUD queue step failed",
      logType: "error",
      extraDataBody: {
        ...details,
        projectId: this.id,
        error: error instanceof Error ? { name: error.name, message: error.message, stack: error.stack } : error
      }
    })
  });

  // Disable this method to work around this issue:
  // https://github.com/bryntum/support/issues/5626
  clearCrudStoresChanges() {}
  async commitAsync() {
    this.trigger("swivucommitstart");
    await super.commitAsync();
    this.trigger("swivucommitend");
  }

  async onCrudRequestFailure(rawResponse, fetchOptions) {
    // needed status code in the requestFail listener in the bryntumContext file
    fetchOptions.swivuRespStatusCode = rawResponse.status;
    await super.onCrudRequestFailure(...arguments);
  }

  async applyResponse(...args) {
    return this.crudQueue.queue(() => {
      return super.applyResponse(...args);
    });
  }

  // Todo: The issue starts to be happening in the bryntum's version 6.1.9 where if there are unmentioned model field exists in a record
  // and applyProjectChanges gets called the willChange function throws an error since it try to find the field definition in the Model
  // which returns null causing the application to break
  // Here is the reported issue on bryntum: https://github.com/bryntum/support/issues/11401
  // Here is the task on Swivu: "Bryntum Bug: Internal Crash On Switch The CC Project (https://admin.dev.swivu.com/swivu-engineering-rjv68i/scheduler?task=98327&tab=general)"
  afterApplyProjectChangesCleanup(changes) {
    for (const storeId in changes) {
      const storeDescriptor = this.getStoreDescriptor(storeId);
      // if that a Store section
      if (storeDescriptor) {
        const
          // Due to this issue better to use lookup on the project instance rather than in global registry
          // https://github.com/bryntum/support/issues/5238
          { store }    = storeDescriptor,
          storeChanges = changes[storeId],
          changedRows  = [...storeChanges.updated ?? [], ...storeChanges.added ?? []];
        // Store might be destroyed, asyncness...
        if (store) {
          // Iterate updated and added rows
          for (const data of changedRows) {
            const record = store.getById(data[store.modelClass.idField]);
            // Record might not have been added e.g. if change was conflicting and got resolved by rejecting
            if (record) {
              // Compare each change on the matching record with the raw data value, unflag change if they match
              for (const fieldName in record.modifications) {
                if (!willChange(fieldName, data, record)) {
                  delete record.meta.modified[fieldName];
                }
              }
            }
            else {
              const
                phantomIdField = this.phantomIdField ?? storeDescriptor.phantomIdField ?? '$PhantomId',
                record = store.getById(data[phantomIdField]);
              if (record) {
                store.added.remove(record);
              }
            }
          }
          for (const data of storeChanges.removed ?? []) {
            store.removed.remove(data[store.modelClass.idField]);
          }
        }
      }
    }
  }
}
